import React from "react";
import { Dialog, DialogContent, styled, useTheme, alpha } from "@mui/material";
import { Box } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Text from "components/mui/Text";
import AppButtonComponent from "components/mui/AppButtonComponent";

// ─── Styled Components ──────────────────────────────────────────────────────────

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        background: "#13172A",
        borderRadius: "16px",
        color: "#fff",
        maxWidth: 520,
        width: "100%",
        margin: theme.spacing(2),
        maxHeight: "calc(100vh - 40px)",
        display: "flex",
        flexDirection: "column",
    },
    "& .MuiBackdrop-root": {
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        backdropFilter: "blur(8px)",
    },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(2.5, 3),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    "&:first-of-type": { paddingTop: theme.spacing(2.5) },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2),
    },
}));

const PlanCard = styled(Box)(({ theme }) => ({
    border: `1.5px solid ${theme.palette.primary.main}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    background: alpha(theme.palette.primary.main, 0.07),
}));

const CheckItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 7,
    marginTop: theme.spacing(0.8),
}));

// ─── Main Component ─────────────────────────────────────────────────────────────

/**
 * Subscription checkout consent screen for the Direct journey.
 *
 * Raised once onboarding is complete and the fee is still owed, as part of the
 * initial Direct journey only. Mandatory — there is no way to dismiss it short
 * of paying; Razorpay itself is only opened from Continue.
 */
const SubscriptionPaymentModal = ({ open = false, onContinue, isSubmitting = false }) => {
    const theme = useTheme();

    return (
        <StyledDialog
            open={open}
            onClose={() => { }} // ignore backdrop click closing
            disableEscapeKeyDown
            maxWidth="sm"
        >
            <StyledDialogContent>

                {/* ── Title ── */}
                <Box sx={{ textAlign: "center", mb: 2.5 }}>
                    <Text sx={{
                        fontWeight: 700,
                        fontSize: { xs: "20px", sm: "22px" },
                        lineHeight: 1.3,
                        color: "#fff",
                        mb: 0.5
                    }}>
                        Activate your Direct Plan
                    </Text>
                    <Text sx={{
                        fontWeight: 400,
                        fontSize: { xs: "13px", sm: "14px" },
                        color: "rgba(255, 255, 255, 0.6)"
                    }}>
                        Your account setup is complete. One last step before you start investing.
                    </Text>
                </Box>

                <PlanCard>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Text sx={{ fontWeight: 700, fontSize: { xs: "15px", sm: "16px" }, color: "#fff" }}>
                                Direct Plan
                            </Text>
                            <Text sx={{ fontWeight: 400, fontSize: "11px", color: "rgba(255,255,255,0.5)", mt: 0.2 }}>
                                Advisory-led · Direct fund
                            </Text>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                            <Text sx={{ fontWeight: 700, fontSize: "20px", color: "#fff" }}>
                                ₹399
                            </Text>
                            <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }}>
                                /mo or 0.1%
                            </Text>
                        </Box>
                    </Box>

                    <CheckItem>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28C76F", mt: "2px", flexShrink: 0 }} />
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>
                            Access to Direct Funds — 0% distributor commission
                        </Text>
                    </CheckItem>

                    <CheckItem>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28C76F", mt: "2px", flexShrink: 0 }} />
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>
                            Expert, SEBI-registered investment advice
                        </Text>
                    </CheckItem>

                    <CheckItem>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28C76F", mt: "2px", flexShrink: 0 }} />
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>
                            Cancel anytime from your profile
                        </Text>
                    </CheckItem>
                </PlanCard>

                <Text sx={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", mt: 1.5, textAlign: "center" }}>
                    You will be taken to Razorpay to authorise the subscription.
                </Text>

                {/* ── Continue Button ── */}
                <AppButtonComponent
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    onClick={() => onContinue?.()}
                    sx={{
                        mt: 2,
                        py: 1.5,
                        borderRadius: "10px",
                        fontWeight: 600,
                        fontSize: "14px",
                        textTransform: "none",
                        backgroundColor: theme.palette.primary.main,
                        "&:hover": { backgroundColor: theme.palette.primary.dark },
                    }}
                >
                    {isSubmitting ? "Opening checkout..." : "Pay ₹399 & continue"}
                </AppButtonComponent>

            </StyledDialogContent>
        </StyledDialog>
    );
};

export default SubscriptionPaymentModal;
